import { Box } from "@mui/material";
import { Typography } from "@platform/design-system";

import { CARD_SX, TIER_ORDER } from "./constants";
import { fmtCurrencyExact } from "./formatters";
import { DonutChart } from "./DonutChart";
import { CardTitle, HelpIcon, LegendKey } from "./primitives";

type StrategicTierOutcome = {
  total: number;
  tier1: number;
  tier2: number;
  nonStrategic: number;
  successPercent: number;
};

type MarketingOutcomesCardProps = {
  migration: StrategicTierOutcome;
  retention: StrategicTierOutcome;
  newLine: StrategicTierOutcome;
};

type MarketingOutcomeChartConfig = {
  label: string;
  description: string;
  data: StrategicTierOutcome;
};

const MARKETING_OUTCOME_CHARTS = ({
  migration,
  retention,
  newLine,
}: MarketingOutcomesCardProps): MarketingOutcomeChartConfig[] => [
  {
    label: "Migration Success",
    description:
      "Shows premium that started with a non-preferred carrier. The chart shows where that premium went. The center number is the share won by Tier 1 or Tier 2 carriers.",
    data: migration,
  },
  {
    label: "Retention Success",
    description:
      "Shows premium that started with a Tier 1 or Tier 2 carrier. The chart shows where that premium stayed or moved. The center number is the share kept with preferred carriers.",
    data: retention,
  },
  {
    label: "Cross Sell Success",
    description:
      "Shows premium for new lines of coverage with no prior carrier. The chart shows which carriers won that new business. The center number is the share won by Tier 1 or Tier 2 carriers.",
    data: newLine,
  },
];

function MarketingOutcomesCard({
  migration,
  retention,
  newLine,
}: MarketingOutcomesCardProps): React.JSX.Element {
  return (
    <Box
      data-testid="marketing-outcomes-card"
      sx={{ ...CARD_SX, flex: 1, minWidth: 0 }}
    >
      <CardTitle>Marketing Outcomes</CardTitle>
      <Box
        sx={{ mt: 2, display: "flex", gap: 4, justifyContent: "space-around" }}
      >
        {MARKETING_OUTCOME_CHARTS({ migration, retention, newLine }).map(
          ({ label, description, data }) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                flex: 1,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 0.5,
                    justifyContent: "center",
                  }}
                >
                  <Typography>
                    <strong>{label}</strong>
                  </Typography>
                  <HelpIcon label={label} description={description} />
                </Box>
                <Typography variant="body-large" color="textSecondary">
                  {fmtCurrencyExact(data.total)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <DonutChart
                  tier1={data.tier1}
                  tier2={data.tier2}
                  nonStrategic={data.nonStrategic}
                  centerLabel={`${data.successPercent}%`}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {TIER_ORDER.map((tier) => (
                    <LegendKey
                      key={tier}
                      tier={tier}
                      value={fmtCurrencyExact(
                        tier === "Tier 1"
                          ? data.tier1
                          : tier === "Tier 2"
                            ? data.tier2
                            : data.nonStrategic,
                      )}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          ),
        )}
      </Box>
    </Box>
  );
}

export { MarketingOutcomesCard };
export type { StrategicTierOutcome };
